import React from 'react';
import classNames from 'classnames';
import Select, { underlinerFunc } from 'ui/select';
import Store from 'store';

import Data from 'data';
import Locale from 'data/locale';
import { D3TooltipCube, D3TooltipItem } from 'tooltips';
import { getIconPath, getTypeIconsPath } from 'common';
import Tooltip from 'ui/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';

const _L = Locale("ui-equipment.js");

const itemSearchFunc = (id, search) => {
  const item = Data.itemById[id];
  if (!item) return false;
  if (item.name.match(search)) return true;
  if (item.required?.custom) {
    if (item.required.custom.format?.match(search)) return true;
    if (item.required.custom.options?.some(id => Data.itemById[id].required?.custom?.format.match(search))) return true;
  }
  return false;
};

function isFollowerItem(item) {
  if (item.required?.custom?.follower === true) return true;
  const set = item.set && Data.itemSets[item.set];
  if (!set) return false;
  return Object.values(set.bonuses).some(list => list.some(bonus => bonus.follower === true));
}

export default React.forwardRef(function ItemSelect({ slot, valueClass, valueQuality, cube, follower, ...props }, ref) {
  const profile = Store.useProfile(profile => slot ? profile : null, [slot]);

  const renderItemValue = React.useCallback(id => (
    <span className={classNames(valueClass, valueQuality && `d3p-quality-${Data.itemById[id]?.quality}`)}>
      {Data.itemById[id]?.name}
    </span>
  ), [valueClass, valueQuality]);

  const renderItemOption = React.useCallback((id, search) => {
    const item = Data.itemById[id];
    if (!item) return null;

    let icon = Data.itemIcons[item.realid || id];
    let type = item.type;
    if ((type === "custom" || type === "customwpn") && slot) {
      const types = (slot === "offhand" ? Data.getOffhandTypes(profile) : Data.itemSlots[slot].types);
      const t = Object.keys(types).find(t => !types[t].classes || types[t].classes.includes(profile.class));
      if (t) {
        type = t;
        icon = Data.itemIcons[Data.itemTypes[t].generic];
      }
    }

    if (icon != null) {
      icon = <span className="d3p-option-icon" style={{backgroundImage: `url(${getTypeIconsPath(type)})`, backgroundPosition: `0 ${-icon[0]}em`}}/>;
    } else if (item.icon) {
      icon = <span className="d3p-option-icon d3p-option-icon-special" style={{backgroundImage: `url(${getIconPath(item.icon)})`}}/>;
    }

    const option = (
      <div className={`d3p-option-item d3p-quality-${item.quality}`}>
        {!!icon && icon}
        <span>{search ? underlinerFunc(item.name, search) : item.name}{!!item.suffix && ` (${item.suffix})`}</span>
        {!!(follower && isFollowerItem(item)) && (
          <Tooltip tooltip={_L`Player gains legendary power on this item when it is equipped on your follower`} touchAction="show">
            <span className="d3p-emanated">
              <FontAwesomeIcon icon={faStar}/>
            </span>
          </Tooltip>
        )}
      </div>
    );

    if (cube) {
      return <D3TooltipCube id={id}>{option}</D3TooltipCube>;
    } else {
      return <D3TooltipItem data={id} slot={slot}>{option}</D3TooltipItem>;
    }
  }, [profile, slot, cube, follower]);

  return <Select
    searchLimit={10}

    renderValue={renderItemValue}
    renderOption={renderItemOption}
    searchFunc={itemSearchFunc}
    searchMiddle={true}

    ref={ref}
    {...props}
  />;
});
